import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { ArrowRight, Waves, Star, Trees, ShieldCheck, Zap, Camera } from "lucide-react";
import Hero from "@/components/sections/Hero";
import BaskingShark from "@/components/sections/BaskingShark";
import EditableText from "@/components/editor/EditableText";
import { cdn, srcSet, thumb } from "@/lib/img";

const COAST_IMG = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/0zj0zjrb_IMG_7577.jpeg";

const HIGHLIGHTS = [
  { icon: ShieldCheck, title: "Two Private Sites", desc: "Site One opens 2027 · Site Two is a longer-term build. Each booked privately, one group at a time.", link: "/land" },
  { icon: Waves, title: "Up to 725 ft of Waterfront", desc: "175 ft at Site One · 550 ft at Site Two — both on the Bay of Fundy's 21-ft tides.", link: "/environment#tides" },
  { icon: Trees, title: "18.5 Acres of Land", desc: "3.5 acres at Site One, 15 acres at Site Two — wooded trails down to the water at both.", link: "/land#trails" },
  { icon: Star, title: "Zero Light Pollution", desc: "Bortle Class 2 — exceptional stargazing at either site.", link: "/environment#stars" },
  { icon: Zap, title: "Fully Off-Grid", desc: "Bring a generator. Self-contained RVs required.", link: "/offgrid" },
  { icon: Camera, title: "Wildlife", desc: "Seals, dolphins, whales, and eagles.", link: "/gallery" },
];

function Section({ children, className = "", id }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.section ref={ref} id={id} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }} className={`wc-section ${className}`}>
      {children}
    </motion.section>
  );
}

export default function HomePage() {
  return (
    <>
      <Hero />

      {/* Experience — Where the forest meets the Bay */}
      <Section className="bg-wc-cream">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="font-accent italic text-wc-teal text-base mb-3">The Experience</p>
            <EditableText contentKey="experience_heading" as="h2"
              className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold max-w-2xl"
              defaultValue="Where the forest meets the Bay..." />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="space-y-6">
              <EditableText contentKey="experience_p1" as="p"
                className="font-body text-wc-slate text-base leading-relaxed"
                defaultValue="You hear it before you see it. Through the spruce and birch, the low rumble of the Bay of Fundy working against the rocks. The air changes — salt and cold stone and something ancient." />
              <EditableText contentKey="experience_p2" as="p"
                className="font-body text-wc-slate text-base leading-relaxed"
                defaultValue="Then the trees open up. The Bay — vast, grey-green, alive — pulls the horizon wide open. Site One opens for the 2027 season, while Site Two is a longer-term build. Both face directly west across the Bay." />
              <EditableText contentKey="experience_p3" as="p"
                className="font-body text-wc-slate text-base leading-relaxed"
                defaultValue="You wake up in the woods with the ocean a hundred feet away. Watch 21 feet of water disappear and return, twice a day. Count satellites instead of sheep. This isn't a campground — it's a place that rearranges your sense of scale." />
              <EditableText contentKey="experience_p4" as="p"
                className="font-body text-wc-slate text-sm leading-relaxed italic"
                defaultValue="No neighbours. No noise. Just you, the trees, and the tides." />
              <Link to="/story" className="inline-flex items-center gap-2 font-body text-wc-teal text-sm hover:text-wc-forest transition-colors pt-2">
                Read Our Story <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative rounded-sm overflow-hidden aspect-[4/3]">
              <img src={cdn(COAST_IMG, 700)} srcSet={srcSet(COAST_IMG)} sizes="(max-width: 1024px) 100vw, 50vw"
                alt="Digby Neck coastline, Bay of Fundy" className="w-full h-full object-cover wc-img" loading="lazy" decoding="async" />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/40 to-transparent">
                <span className="font-accent italic text-white/80 text-sm">
                  The coastline at Whale Cove — 725 feet across two sites
                </span>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Basking shark viral video — proof-of-concept for wildlife from shore */}
      <BaskingShark />

      {/* Property highlights */}
      <Section className="bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="font-accent italic text-wc-teal text-base mb-3">The Property</p>
            <EditableText contentKey="property_heading" as="h2"
              className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4"
              defaultValue="Two sites. One shared retreat." />
            <EditableText contentKey="property_subtitle" as="p"
              className="font-body text-wc-slate text-base sm:text-lg max-w-2xl mx-auto leading-relaxed"
              defaultValue="18.5 acres total. Up to 725 feet of Bay of Fundy waterfront. Site One opens for guests in the 2027 season. Site Two is a longer-term build — the access road is a major project that will follow in future seasons. Through 2026, we're on the land ourselves, testing every detail with family and friends while we walk permits through the municipality." />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {HIGHLIGHTS.map((h, i) => (
              <Link key={i} to={h.link}
                className="group flex gap-4 p-5 rounded-md bg-wc-cream/50 border border-wc-driftwood/20 hover:border-wc-teal/30 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 rounded-sm bg-wc-teal/10 flex items-center justify-center">
                  <h.icon className="w-5 h-5 text-wc-teal" />
                </div>
                <div>
                  <h3 className="font-display text-wc-forest text-base font-semibold mb-1 group-hover:text-wc-teal transition-colors">{h.title}</h3>
                  <p className="font-body text-wc-slate text-sm">{h.desc}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/land" className="inline-flex items-center gap-2 font-body text-wc-teal text-sm hover:text-wc-forest transition-colors">
              Explore The Land <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </Section>

      {/* Gallery preview */}
      <Section className="bg-wc-cream">
        <div className="max-w-6xl mx-auto text-center">
          <EditableText contentKey="gallery_heading" as="h2"
            className="font-display text-wc-forest text-3xl sm:text-4xl font-semibold mb-8"
            defaultValue="See for yourself." />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[
              { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/z88nvx3c_IMG_7573.jpeg", alt: "Rocky shoreline at Whale Cove on the Bay of Fundy, Digby Neck NS" },
              { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/xkvkomv6_IMG_7542%20%281%29.jpeg", alt: "Misty morning on the Bay of Fundy coast at Whale Cove" },
              { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/je5ne52t_IMG_8958.jpeg", alt: "Bay of Fundy shoreline looking west — Whale Cove Off Grid" },
              { src: "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/no751j3x_IMG_8970.jpeg", alt: "Forested headland meeting the Bay of Fundy — Digby Neck, Nova Scotia" },
            ].map((photo, i) => (
              <div key={i} className="rounded-sm overflow-hidden aspect-square group">
                <img src={thumb(photo.src, 500)} srcSet={`${thumb(photo.src, 400)} 400w, ${thumb(photo.src, 700)} 700w`} sizes="(max-width: 768px) 50vw, 25vw"
                  alt={photo.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy" decoding="async" />
              </div>
            ))}
          </div>
          <Link to="/gallery" className="inline-flex items-center gap-2 font-body text-sm text-wc-teal border border-wc-teal/30 px-6 py-3 rounded-sm hover:bg-wc-teal hover:text-wc-cream transition-all">
            View Full Gallery <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Section>

      {/* CTA */}
      <Section className="bg-wc-forest">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-[#F0EFEB] text-3xl sm:text-4xl font-semibold mb-4">Follow the build.</h2>
          <p className="font-body text-wc-driftwood text-base mb-8">Site One opens for the 2027 season. Through 2026 we're on the land ourselves — testing every detail with family and friends, walking permits through the municipality. Follow along, and we'll reach out personally when the gates open.</p>
          <Link to="/waitlist" className="inline-block font-body text-sm px-8 py-3.5 bg-wc-teal text-wc-forest rounded-sm hover:bg-wc-cream transition-colors">
            Follow the Build
          </Link>
        </div>
      </Section>
    </>
  );
}
